import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subscription, catchError, interval, map, of, startWith, tap } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface MaintenanceStatus {
  maintenance: boolean;
  message: string | null;
  endsAt: string | null;
}

@Injectable({ providedIn: 'root' })
export class MaintenanceService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private pollingSubscription?: Subscription;

  readonly enabled = signal(false);
  readonly message = signal<string | null>(null);
  readonly endsAt = signal<string | null>(null);

  checkStatus(): Observable<boolean> {
    return this.http
      .get<MaintenanceStatus>(`${environment.apiUrl}/maintenance/status`, {
        headers: { 'Cache-Control': 'no-cache' },
      })
      .pipe(
        tap((status) => {
          this.enabled.set(status.maintenance);
          this.message.set(status.message);
          this.endsAt.set(status.endsAt);
        }),
        map((status) => status.maintenance),
        catchError(() => of(this.enabled())),
      );
  }

  startPolling(): void {
    if (this.pollingSubscription) return;

    this.pollingSubscription = interval(15_000)
      .pipe(startWith(0))
      .subscribe(() => {
        this.checkStatus().subscribe((enabled) => {
          const onMaintenancePage = this.router.url.split('?')[0] === '/maintenance';
          if (enabled && !onMaintenancePage) void this.router.navigateByUrl('/maintenance');
          if (!enabled && onMaintenancePage) void this.router.navigateByUrl('/login');
        });
      });
  }

  stopPolling(): void {
    this.pollingSubscription?.unsubscribe();
    this.pollingSubscription = undefined;
  }
}
